/**
 * EditableNumberCell - Componente para célula editável de número na tabela
 */
import React, { useState, useEffect } from 'react';

interface EditableCellProps {
  value: number;
  rowIndex: number;
  fieldName: string;
  isReadOnly: boolean;
  isModified: boolean;
  onUpdate: (rowIndex: number, field: string, value: number) => void;
}

export const EditableNumberCell: React.FC<EditableCellProps> = ({
  value,
  rowIndex,
  fieldName,
  isReadOnly,
  isModified,
  onUpdate
}) => {
  const [localValue, setLocalValue] = useState(value.toString());

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleCommit = (newValueStr: string) => {
    const numValue = parseInt(newValueStr);
    if (!isNaN(numValue) && numValue !== value) {
      onUpdate(rowIndex, fieldName, numValue);
    } else if (isNaN(numValue)) {
      setLocalValue(value.toString());
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = (parseInt(localValue) || 0) + 1;
    setLocalValue(newValue.toString());
    handleCommit(newValue.toString());
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const current = parseInt(localValue) || 0;
    const newValue = Math.max(0, current - 1);
    setLocalValue(newValue.toString());
    handleCommit(newValue.toString());
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    handleCommit(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCommit(localValue);
      (e.target as HTMLInputElement).blur();
    }
  };

  if (isReadOnly) {
    return (
      <div
        className={`px-2 py-1 text-sm text-center rounded transition-all ${
          isModified
            ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50'
            : 'border-2 border-transparent'
        }`}
      >
        {value}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleDecrement}
        className="w-7 h-7 flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold text-base transition-colors focus:outline-none"
        title="Diminuir (-1)"
      >
        −
      </button>

      <input
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-16 h-7 px-1 text-center text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        min="0"
      />

      <button
        type="button"
        onClick={handleIncrement}
        className="w-7 h-7 flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold text-base transition-colors focus:outline-none"
        title="Aumentar (+1)"
      >
        +
      </button>
    </div>
  );
};
